import { useDebounceEffect } from 'ahooks';
import { useCallback, useRef, useState } from 'react';

import { ModuleSearchPostsReq, ModuleSupportCenterPost } from '@src/api/generated/data-contracts';
import { postSupportSearch } from '@src/api/generated/Support';
import { BASE_LANGUAGE } from '@src/pages/supportCenter/constants/language';

// 平台类型
export type PlatformCategory = 'all' | 'publisher' | 'advertiser';

// 单个平台的分页状态
export interface PlatformPaginationState {
  results: ModuleSupportCenterPost[];
  loading: boolean;
  loadingMore: boolean;
  totalCount: number;
  currentPage: number;
  hasMore: boolean;
}

// 所有平台的状态
export interface SupportSearchPaginationState {
  all: PlatformPaginationState;
  publisher: PlatformPaginationState;
  advertiser: PlatformPaginationState;
}

export interface UseSupportSearchPaginationReturn {
  state: SupportSearchPaginationState;
  // 当前「关键词 + 语言」的第一页是否还没搜完（含防抖期）：初始 results=[] 与"搜过没结果"同形，
  // 少了它进结果页会先闪一下"没找到匹配结果"
  pending: boolean;
  loadMore: (platform: PlatformCategory) => Promise<void>;
  reset: () => void;
}

const PAGE_SIZE = 10;

// 初始化单个平台状态
const createInitialPlatformState = (): PlatformPaginationState => ({
  results: [],
  loading: false,
  loadingMore: false,
  totalCount: 0,
  currentPage: 1,
  hasMore: false,
});

// 初始化所有平台状态
const createInitialState = (): SupportSearchPaginationState => ({
  all: createInitialPlatformState(),
  publisher: createInitialPlatformState(),
  advertiser: createInitialPlatformState(),
});

/**
 * SupportSearch 页面搜索 hook
 * 按平台分别请求数据，支持分页加载，每次加载 10 个
 *
 * @param language 搜索语言。「全部结果页」要跟随发起端语言（内页发起＝内页语言、
 *   首页发起＝英文），所以由调用方从 URL 参数读出后显式传入，不在这里读 localStorage。
 */
function useSupportSearchPagination(
  keyword: string,
  language: string = BASE_LANGUAGE,
): UseSupportSearchPaginationReturn {
  const [state, setState] = useState<SupportSearchPaginationState>(createInitialState);

  // 用于取消过期请求
  const searchVersionRef = useRef(0);

  // 已经搜完第一页的那一组「关键词 + 语言」，空串表示一组都还没搜过。
  // 用它把"还没搜"与"搜过没结果"区分开（见 pending 注释）。
  const [settledToken, setSettledToken] = useState('');
  const searchToken = `${language}\u0000${keyword.trim()}`;
  const pending = !!keyword.trim() && settledToken !== searchToken;

  // 搜索单个平台的第一页
  const searchPlatformFirstPage = useCallback(async (
    searchKeyword: string,
    platform: PlatformCategory,
    currentVersion: number,
  ) => {
    try {
      const searchParams: ModuleSearchPostsReq = {
        keyword: searchKeyword.trim(),
        page: 1,
        pageSize: PAGE_SIZE,
        withContent: true,
        searchType: 'both',
        language,
        // all 不传 platform 参数，表示搜索全部
        ...(platform !== 'all' && { platform }),
      };

      const response = await postSupportSearch(searchParams);

      // 检查是否为最新请求
      if (currentVersion !== searchVersionRef.current) {
        return;
      }

      if (response.code === 0 && response.data) {
        const items = response.data.items || [];
        const totalNumber = response.data.pageInfo?.totalNumber || 0;
        setState(prev => ({
          ...prev,
          [platform]: {
            results: items,
            loading: false,
            loadingMore: false,
            totalCount: totalNumber,
            currentPage: 1,
            hasMore: items.length < totalNumber,
          },
        }));
      } else {
        console.error(`Search ${platform} failed:`, response.msg);
        setState(prev => ({
          ...prev,
          [platform]: {
            ...createInitialPlatformState(),
            loading: false,
          },
        }));
      }
    } catch (error) {
      // 检查是否为最新请求
      if (currentVersion !== searchVersionRef.current) {
        return;
      }
      console.error(`Search ${platform} error:`, error);
      setState(prev => ({
        ...prev,
        [platform]: {
          ...createInitialPlatformState(),
          loading: false,
        },
      }));
    }
  }, [language]);

  // 首次搜索 - 同时请求三个平台的第一页
  const searchFirstPage = useCallback(async (searchKeyword: string) => {
    if (!searchKeyword.trim()) {
      setState(createInitialState());
      return;
    }

    // 增加版本号，用于取消过期请求
    searchVersionRef.current += 1;
    const currentVersion = searchVersionRef.current;
    const currentToken = `${language}\u0000${searchKeyword.trim()}`;

    // 设置所有平台为加载中；**保留上一次的计数**：重新搜索时左侧「All results（n）」
    // 不该先消失再出现（列表本身会在结果回来后整体替换，无需在这里清空计数）
    setState(prev => ({
      all: { ...createInitialPlatformState(), loading: true, totalCount: prev.all.totalCount },
      publisher: { ...createInitialPlatformState(), loading: true, totalCount: prev.publisher.totalCount },
      advertiser: { ...createInitialPlatformState(), loading: true, totalCount: prev.advertiser.totalCount },
    }));

    // 并行请求三个平台的数据
    await Promise.all([
      searchPlatformFirstPage(searchKeyword, 'all', currentVersion),
      searchPlatformFirstPage(searchKeyword, 'publisher', currentVersion),
      searchPlatformFirstPage(searchKeyword, 'advertiser', currentVersion),
    ]);

    // 三个平台的第一页都落地（成功、失败各有兜底分支）后才算"这组关键词搜完了"；
    // 期间若有更新的搜索发出（版本号变了），则本次不认领。
    if (searchVersionRef.current === currentVersion) {
      setSettledToken(currentToken);
    }
  }, [searchPlatformFirstPage, language]);

  // 加载更多 - 针对特定平台
  const loadMore = useCallback(async (platform: PlatformCategory) => {
    const platformState = state[platform];

    if (platformState.loading || platformState.loadingMore || !platformState.hasMore || !keyword.trim()) {
      return;
    }

    const nextPage = platformState.currentPage + 1;
    // 记录发起时的搜索版本：加载期间若换了关键词/语言（版本号会变），本次结果作废，
    // 否则旧关键词的第二页会被拼进新关键词的列表
    const currentVersion = searchVersionRef.current;

    // 设置该平台为加载更多中
    setState(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        loadingMore: true,
      },
    }));

    try {
      const searchParams: ModuleSearchPostsReq = {
        keyword: keyword.trim(),
        page: nextPage,
        pageSize: PAGE_SIZE,
        withContent: true,
        searchType: 'both',
        language,
        ...(platform !== 'all' && { platform }),
      };

      const response = await postSupportSearch(searchParams);

      // 期间换了关键词/语言：这次返回的是过期数据，丢弃（否则会拼进新列表）
      if (currentVersion !== searchVersionRef.current) return;

      if (response.code === 0 && response.data) {
        const newItems = response.data.items || [];
        const totalNumber = response.data.pageInfo?.totalNumber || 0;
        setState(prev => ({
          ...prev,
          [platform]: {
            ...prev[platform],
            results: [...prev[platform].results, ...newItems],
            loadingMore: false,
            currentPage: nextPage,
            totalCount: totalNumber,
            hasMore: prev[platform].results.length + newItems.length < totalNumber,
          },
        }));
      } else {
        console.error(`Load more ${platform} failed:`, response.msg);
        setState(prev => ({
          ...prev,
          [platform]: {
            ...prev[platform],
            loadingMore: false,
          },
        }));
      }
    } catch (error) {
      if (currentVersion !== searchVersionRef.current) return;
      console.error(`Load more ${platform} error:`, error);
      setState(prev => ({
        ...prev,
        [platform]: {
          ...prev[platform],
          loadingMore: false,
        },
      }));
    }
  }, [keyword, state, language]);

  // 重置搜索状态
  const reset = useCallback(() => {
    searchVersionRef.current += 1;
    setState(createInitialState());
    setSettledToken('');
  }, []);

  // 防抖搜索 - keyword 变化时自动搜索第一页
  useDebounceEffect(
    () => {
      if (keyword) {
        searchFirstPage(keyword);
      } else {
        reset();
      }
    },
    // language 必须留在依赖里：换语言等于换数据源，要按新语言重搜第一页。
    // 本页自身不提供语言切换，但同一次挂载内语言仍可能变 —— 例如「内页(中文)进结果页 →
    // Back 回首页 → 再从首页 Banner 进结果页」，此时语言由 zh 回到 en、关键词可能一模一样，
    // 少了这个依赖就不会重搜（拿到的是上一个语言的结果）。
    [keyword, language],
    { wait: 300 },
  );

  return {
    state,
    pending,
    loadMore,
    reset,
  };
}

export default useSupportSearchPagination;
